import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import {
    API_BASE_URL,
    REVERB_APP_KEY,
    REVERB_AUTH_ENDPOINT,
    REVERB_URL,
} from '../config';

declare global {
    interface Window {
        Pusher: typeof Pusher;
    }
}

type RealtimeEventHandler<TPayload> = (payload: TPayload) => void;

interface SubscribeToPrivateEventOptions<TPayload> {
    authToken: string;
    channelName: string;
    eventName: string;
    handler: RealtimeEventHandler<TPayload>;
    onError?: (message: string) => void;
    onConnectionStateChange?: (connected: boolean) => void;
}

type RealtimeChannel = ReturnType<Echo<'reverb'>['private']>;

let echoInstance: Echo<'reverb'> | null = null;
let currentToken: string | null = null;
let activeListenerCount = 0;

const activeChannelListeners = new Map<string, number>();

function parseRealtimeUrl(): {
    host: string;
    port: number;
    secure: boolean;
} {
    try {
        const url = new URL(REVERB_URL || API_BASE_URL);
        const secure = url.protocol === 'wss:' || url.protocol === 'https:';
        const fallbackPort = secure ? 443 : 80;

        return {
            host: url.hostname,
            port: Number(url.port || fallbackPort),
            secure,
        };
    } catch {
        return {
            host: 'localhost',
            port: 80,
            secure: false,
        };
    }
}

function getEchoInstance(token: string): Echo<'reverb'> | null {
    if (!REVERB_APP_KEY) {
        return null;
    }

    if (echoInstance && currentToken === token) {
        return echoInstance;
    }

    if (echoInstance && currentToken !== token) {
        echoInstance.disconnect();
        echoInstance = null;
        currentToken = null;
        activeListenerCount = 0;
        activeChannelListeners.clear();
    }

    const connection = parseRealtimeUrl();

    window.Pusher = Pusher;

    echoInstance = new Echo<'reverb'>({
        broadcaster: 'reverb',
        key: REVERB_APP_KEY,
        wsHost: connection.host,
        wsPort: connection.port,
        wssPort: connection.port,
        forceTLS: connection.secure,
        enabledTransports: ['ws', 'wss'],
        authEndpoint: REVERB_AUTH_ENDPOINT,
        auth: {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
        },
    });

    currentToken = token;

    return echoInstance;
}

function retainChannel(channelName: string): void {
    activeListenerCount += 1;
    activeChannelListeners.set(channelName, (activeChannelListeners.get(channelName) ?? 0) + 1);
}

function releaseChannel(channelName: string): void {
    activeListenerCount = Math.max(0, activeListenerCount - 1);

    const currentChannelListeners = activeChannelListeners.get(channelName) ?? 0;

    if (currentChannelListeners <= 1) {
        activeChannelListeners.delete(channelName);
        echoInstance?.leave(channelName);
    } else {
        activeChannelListeners.set(channelName, currentChannelListeners - 1);
    }

    if (activeListenerCount === 0 && echoInstance) {
        echoInstance.disconnect();
        echoInstance = null;
        currentToken = null;
    }
}

function buildRealtimeErrorMessage(error: unknown): string {
    if (
        error &&
        typeof error === 'object' &&
        'status' in error &&
        Number((error as { status?: unknown }).status) === 403
    ) {
        return 'No fue posible autenticar la suscripción realtime del contrato.';
    }

    return 'No se pudo establecer la conexión en tiempo real.';
}

function bindSubscriptionError(
    channel: RealtimeChannel,
    onError?: (message: string) => void,
): (() => void) | null {
    if (!onError) {
        return null;
    }

    const subscription = (channel as RealtimeChannel & {
        subscription?: {
            bind: (event: string, callback: CallableFunction) => void;
            unbind: (event: string, callback: CallableFunction) => void;
        };
    }).subscription;

    if (!subscription) {
        return null;
    }

    const handler = (error: unknown) => {
        onError(buildRealtimeErrorMessage(error));
    };

    subscription.bind('pusher:subscription_error', handler);

    return () => {
        subscription.unbind('pusher:subscription_error', handler);
    };
}

export function subscribeToPrivateEvent<TPayload>({
    authToken,
    channelName,
    eventName,
    handler,
    onError,
    onConnectionStateChange,
}: SubscribeToPrivateEventOptions<TPayload>): () => void {
    const echo = getEchoInstance(authToken);

    if (!echo) {
        onConnectionStateChange?.(false);
        onError?.('Realtime no está configurado para este entorno.');
        return () => undefined;
    }

    const channel = echo.private(channelName);
    const wrappedHandler = (payload: unknown) => {
        handler(payload as TPayload);
    };

    retainChannel(channelName);
    channel.listen(eventName, wrappedHandler);

    const unbindSubscriptionError = bindSubscriptionError(channel, onError);
    const unbindConnectionState = onConnectionStateChange
        ? echo.connector.onConnectionChange((status) => {
            onConnectionStateChange(status === 'connected');
        })
        : null;

    onConnectionStateChange?.(echo.connector.connectionStatus() === 'connected');

    return () => {
        channel.stopListening(eventName, wrappedHandler);
        unbindSubscriptionError?.();
        unbindConnectionState?.();
        releaseChannel(channelName);
    };
}

export function disconnectRealtime(): void {
    echoInstance?.disconnect();
    echoInstance = null;
    currentToken = null;
    activeListenerCount = 0;
    activeChannelListeners.clear();
}